import { find } from "lodash";
import { processText } from "../TemplateUtils";
import type { DecisionGraph } from "./DecisionGraph";
import { getNodeResult } from "./GraphMisc";

/**
 * Session views over a DecisionGraph: the interactions by phase (getInteractionsByPhase's).
 */

type Phases = Record<string, { attribute?: string; interactions: any[] }>;

/**
 * getInteractionsByPhase over a DecisionGraph: walks from the goal over ids, reading phase and trigger rule data per
 * template, and makes node objects only for the nodes that have triggers.
 */
export const decisionGraphInteractionsByPhase = (
  session: { goal?: string; relevantAttributes?: string[] },
  graph: DecisionGraph,
  release?: { relationships?: any[]; variables?: any[]; locale?: string },
): Phases => {
  const phases: Phases = { _nophase: { interactions: [] } };
  const goal = session.goal === undefined ? undefined : graph.ref(session.goal);
  if (goal === undefined || !graph.isListed(goal)) return phases;

  const phaseFlags = graph.templateField("phase");
  const phaseNames = graph.templateField("phaseName") as (string | undefined)[];
  const triggers = graph.templateField("triggers") as (any[] | undefined)[];
  const relevant = (path: string) => (session.relevantAttributes?.indexOf(path) ?? -1) !== -1;
  const seen = new Uint8Array(graph.idBound());

  // depth first, in the order the recursive walk visited (children pushed in reverse)
  const stack: [number, string][] = [[goal, "_nophase"]];
  while (stack.length > 0) {
    const [id, phase] = stack.pop()!;
    if (!graph.isListed(id) || graph.field(id, "hidden")) continue;
    const t = graph.template(id);
    let currentPhase = phase;
    const path = graph.deref(id);
    const phaseName = phaseNames[t];
    if (phaseFlags[t] && phaseName) {
      if (!phases[phaseName]) phases[phaseName] = { attribute: path, interactions: [] };
      currentPhase = phaseName;
    }
    const nodeTriggers = triggers[t];
    if (nodeTriggers && !find(phases[currentPhase].interactions, { attribute: path })) {
      const node = graph.node(id) as any;
      const result = getNodeResult(node);
      nodeTriggers.forEach((trigger: any, i: number) => {
        const triggerInfo = node.triggered?.[i];
        let isRelevant = true;
        if (result === false) isRelevant = false;
        else if (!triggerInfo && !relevant(path)) isRelevant = false;
        else if ((result === undefined || result === null) && trigger.type === "task" && !relevant(path)) {
          isRelevant = false;
        }
        const nodeContext =
          node.entity !== "global" ? { entity: node.entity, index: node.index, parent: node.parent_path } : {};
        const title =
          typeof trigger.title === "string"
            ? processText(trigger.title, nodeContext, {
                graph,
                release,
              })
            : undefined;
        const outcome = trigger.outcome === undefined ? undefined : graph.ref(trigger.outcome);
        phases[currentPhase].interactions.push({
          type: trigger.type,
          title: title ? title.text : trigger.title,
          outcome: outcome === undefined ? undefined : getNodeResult(graph.node(outcome) as any),
          created: triggerInfo?.when,
          outcomeAttribute: trigger.outcome,
          interaction: triggerInfo?.interaction,
          assignedTo: "",
          attribute: path,
          relevant: isRelevant,
        });
      });
    }
    if (seen[id]) continue;
    seen[id] = 1;
    const children = graph.successors(id);
    for (let i = children.length - 1; i >= 0; i--) stack.push([children[i], currentPhase]);
  }
  return phases;
};
